import { useQuery } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';
import { useCurrentUser } from '@bonusly/who-am-i';

import { QueryKey } from '../../../types/growth-query-key';
import { MeetingV2ListData, MeetingV2ListDTO } from '../lib/meeting-v2';

/**
 * Returns all active meetings where the current user is a participant.
 * Uses lightweight list view for performance.
 *
 * Note: This hook returns MeetingV2ListDTO (lightweight) for index pages.
 * When navigating to a meeting detail page, the full meeting data will be
 * fetched by useFetchMeeting which returns MeetingV2DTO.
 */
interface UseUserMeetingsV2Options {
	enabled?: boolean;
}

export const useUserMeetingsV2 = ({ enabled = true }: UseUserMeetingsV2Options = {}) => {
	const http = useHttpClient();
	const user = useCurrentUser();

	return useQuery({
		queryKey: [QueryKey.MeetingsV2, user.id],
		queryFn: async () => {
			const res = await http.get<MeetingV2ListData[]>('/one_on_ones/meetings_v2', {
				headers: { 'Key-Inflection': 'camel' },
				params: { participant_id: user.id },
			});

			return res.data.map((data) => new MeetingV2ListDTO(data));
		},
		// Always refetch when component mounts to ensure fresh data after navigation
		refetchOnMount: 'always',
		// Always refetch when window regains focus to catch changes made in other tabs/windows
		refetchOnWindowFocus: 'always',
		enabled,
	});
};
